import React, { useEffect, useRef, useState } from "react";

function parseFbResizeHeight(data: unknown): number | null {
    if (data == null) return null;
    if (typeof data === "number" && Number.isFinite(data) && data > 50) return data;
    if (typeof data === "string") {
        const n = parseInt(data, 10);
        if (!isNaN(n) && n > 50) return n;
        try {
            return parseFbResizeHeight(JSON.parse(data));
        } catch {
            return null;
        }
    }
    if (Array.isArray(data)) {
        let best = 0;
        for (const v of data) {
            const h = typeof v === "number" ? v : parseFbResizeHeight(v);
            if (h && h > best) best = h;
        }
        return best > 50 ? best : null;
    }
    if (typeof data === "object") {
        const o = data as Record<string, unknown>;
        if ((o.type === "resize" || o.method === "resize") && typeof o.height === "number") {
            return o.height > 50 ? o.height : null;
        }
        for (const key of ["height", "frameHeight", "frame_height"] as const) {
            const v = o[key];
            if (typeof v === "number" && v > 50) return v;
            if (typeof v === "string") {
                const n = parseInt(v, 10);
                if (!isNaN(n) && n > 50) return n;
            }
        }
        for (const key of ["payload", "data"] as const) {
            const inner = parseFbResizeHeight(o[key]);
            if (inner != null) return inner;
        }
    }
    return null;
}

export type FacebookPostEmbedProps = {
    url: string;
    width: number | string;
};

export function FacebookPostEmbed({ url, width }: FacebookPostEmbedProps): React.ReactElement {
    const [height, setHeight] = useState(500);
    const maxHeightRef = useRef(0);

    const numWidth = typeof width === "number" ? width : 700;
    const src = `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(
        url
    )}&show_text=true&width=${numWidth}`;

    useEffect(() => {
        setHeight(500);
        maxHeightRef.current = 0;
    }, [url]);

    useEffect(() => {
        if (process.env.BUILD_TARGET === "server") return;

        const onMessage = (e: MessageEvent) => {
            if (!/^https:\/\/(www|web|m)\.facebook\.com/.test(e.origin)) return;
            const h = parseFbResizeHeight(e.data);
            if (h && h > 50) {
                const next = Math.ceil(h) + 20;
                maxHeightRef.current = Math.max(maxHeightRef.current, next);
                setHeight((prev) => Math.max(prev, maxHeightRef.current));
            }
        };
        window.addEventListener("message", onMessage);

        const fallback = window.setTimeout(() => {
            if (maxHeightRef.current < 530) {
                setHeight((h) => Math.max(h, Math.max(1400, Math.floor(window.innerHeight * 1.5))));
            }
        }, 1000);

        return () => {
            window.removeEventListener("message", onMessage);
            clearTimeout(fallback);
        };
    }, [url]);

    return (
        <iframe
            key={url}
            src={src}
            width={numWidth}
            height={height}
            title="Facebook príspevok"
            style={{ border: "none", overflow: "hidden", width: "100%", maxWidth: "100%", display: "block" }}
            scrolling="no"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        />
    );
}
